import React, { useState, useEffect } from 'react';
import {
  getDiscordPresets,
  createDiscordPreset,
  updateDiscordPreset,
  deleteDiscordPreset,
  sendDiscordPreset,
  sendCustomEmbed
} from '../../api';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const EMPTY_FORM = {
  presetName: '',
  guildId: '',
  channelId: '',
  content: '',
  title: '',
  description: '',
  color: '#5865F2',
  authorName: '',
  authorIconURL: '',
  authorURL: '',
  footerText: '',
  footerIconURL: '',
  thumbnailURL: '',
  imageURL: '',
  fields: []
};

export default function AdminDiscordPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [useEmbed, setUseEmbed] = useState(true);

  useEffect(() => { loadPresets(); }, []);

  const loadPresets = async () => {
    try {
      const res = await getDiscordPresets();
      setPresets(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const formToPayload = () => ({
    name: form.presetName,
    guildId: form.guildId,
    channelId: form.channelId,
    content: form.content,
    embed: useEmbed ? {
      title: form.title,
      description: form.description,
      color: form.color,
      authorName: form.authorName,
      authorIconURL: form.authorIconURL,
      authorURL: form.authorURL,
      footerText: form.footerText,
      footerIconURL: form.footerIconURL,
      thumbnailURL: form.thumbnailURL,
      imageURL: form.imageURL,
      fields: form.fields
    } : null
  });

  const presetToForm = (p) => ({
    presetName: p.name || '',
    guildId: p.guildId || '',
    channelId: p.channelId || '',
    content: p.content || '',
    title: p.embed?.title || '',
    description: p.embed?.description || '',
    color: p.embed?.color || '#5865F2',
    authorName: p.embed?.authorName || p.embed?.author?.name || '',
    authorIconURL: p.embed?.authorIconURL || p.embed?.author?.iconURL || p.embed?.author?.icon_url || '',
    authorURL: p.embed?.authorURL || p.embed?.author?.url || '',
    footerText: p.embed?.footerText || p.embed?.footer?.text || '',
    footerIconURL: p.embed?.footerIconURL || p.embed?.footer?.iconURL || p.embed?.footer?.icon_url || '',
    thumbnailURL: p.embed?.thumbnailURL || p.embed?.thumbnail?.url || '',
    imageURL: p.embed?.imageURL || p.embed?.image?.url || '',
    fields: p.embed?.fields || []
  });

  const handleSavePreset = async () => {
    if (!form.presetName.trim()) { setError('Preset name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = formToPayload();
      if (editingId) {
        await updateDiscordPreset(editingId, payload);
        setSuccess('Preset updated!');
      } else {
        await createDiscordPreset(payload);
        setSuccess('Preset saved!');
      }
      await loadPresets();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true); setError('');
    try {
      const payload = formToPayload();
      await sendCustomEmbed({
        guildId: payload.guildId,
        channelId: payload.channelId,
        content: payload.content,
        embed: payload.embed
      });
      setSuccess('Embed sent successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send embed');
    } finally {
      setSending(false);
    }
  };

  const handleEdit = (preset) => {
    setForm(presetToForm(preset));
    setEditingId(preset._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await deleteDiscordPreset(id);
      await loadPresets();
      if (editingId === id) resetForm();
      setSuccess('Preset deleted.'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete preset');
    }
  };

  const handleSendPreset = async (id) => {
    try {
      await sendDiscordPreset(id);
      setSuccess('Preset sent!'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send preset');
    }
  };

  const handleCopyPreset = async (preset) => {
    try {
      const f = presetToForm(preset);
      await createDiscordPreset({
        name: `Copy of ${preset.name}`,
        guildId: f.guildId,
        channelId: f.channelId,
        content: f.content,
        embed: {
          title: f.title, description: f.description, color: f.color,
          authorName: f.authorName, authorIconURL: f.authorIconURL, authorURL: f.authorURL,
          footerText: f.footerText, footerIconURL: f.footerIconURL,
          thumbnailURL: f.thumbnailURL, imageURL: f.imageURL, fields: f.fields
        }
      });
      await loadPresets();
      setSuccess(`'${preset.name}' copied!`); setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy preset');
    }
  };

  // Fields management
  const addField = () => setForm(f => ({ ...f, fields: [...f.fields, { name: '', value: '', inline: false }] }));
  const removeField = (i) => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));
  const updateField = (i, key, val) => setForm(f => {
    const fields = [...f.fields];
    fields[i] = { ...fields[i], [key]: val };
    return { ...f, fields };
  });

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Admin Discord</h2>
          <div className="subtitle">Create and manage Discord embed presets</div>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

        {/* ── Form ── */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>
              {editingId ? '✏️ Edit Preset' : '➕ New Embed / Preset'}
            </h3>
            {editingId && (
              <button className="btn btn-ghost btn-sm" onClick={resetForm} style={{ color: 'var(--text-muted)' }}>
                <XMarkIcon className="icon" /> Cancel edit
              </button>
            )}
          </div>

          {/* Meta */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Preset name</label>
              <input className="form-input" placeholder="e.g. Weekly announcement" value={form.presetName} onChange={set('presetName')} />
            </div>
            <div className="form-group">
              <label>Guild ID</label>
              <input className="form-input" placeholder="123456789012345678" value={form.guildId} onChange={e => setForm(f => ({ ...f, guildId: e.target.value.replace(/\D/g,'') }))} maxLength={20} />
            </div>
            <div className="form-group">
              <label>Channel ID</label>
              <input className="form-input" placeholder="123456789012345678" value={form.channelId} onChange={e => setForm(f => ({ ...f, channelId: e.target.value.replace(/\D/g,'') }))} maxLength={20} />
            </div>
            <div className="form-group">
              <label>Embed color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 36, flexShrink: 0, cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <div style={{ width: '100%', height: '100%', background: form.color }} />
                  <input type="color" value={form.color} onChange={set('color')} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
                </label>
                <input
                  className="form-input"
                  value={form.color}
                  onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setForm(f => ({ ...f, color: v })); }}
                  placeholder="#5865F2"
                  maxLength={7}
                  style={{ width: 110, fontFamily: 'monospace' }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="form-group">
            <label>Content <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(plain text above embed)</span></label>
            <textarea className="form-input" rows={2} placeholder="@here New announcement!" value={form.content} onChange={set('content')} />
          </div>

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 12px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>EMBED</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={useEmbed} onChange={e => setUseEmbed(e.target.checked)} />
              Include embed
            </label>
          </div>

          {useEmbed && (<>
          {/* Title + description */}
          <div className="form-group">
            <label>Title</label>
            <input className="form-input" placeholder="Embed title" value={form.title} onChange={set('title')} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" rows={3} placeholder="Embed description..." value={form.description} onChange={set('description')} />
          </div>

          {/* Author */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, marginTop: 4 }}>AUTHOR</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Name</label>
              <input className="form-input" placeholder="Author name" value={form.authorName} onChange={set('authorName')} />
            </div>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Icon URL</label>
              <input className="form-input" placeholder="https://..." value={form.authorIconURL} onChange={set('authorIconURL')} />
            </div>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Author URL</label>
              <input className="form-input" placeholder="https://..." value={form.authorURL} onChange={set('authorURL')} />
            </div>
          </div>

          {/* Footer */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>FOOTER</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Text</label>
              <input className="form-input" placeholder="Footer text" value={form.footerText} onChange={set('footerText')} />
            </div>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Icon URL</label>
              <input className="form-input" placeholder="https://..." value={form.footerIconURL} onChange={set('footerIconURL')} />
            </div>
          </div>

          {/* Images */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>IMAGES</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Thumbnail URL</label>
              <input className="form-input" placeholder="https://..." value={form.thumbnailURL} onChange={set('thumbnailURL')} />
            </div>
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Image URL (large)</label>
              <input className="form-input" placeholder="https://..." value={form.imageURL} onChange={set('imageURL')} />
            </div>
          </div>

          {/* Fields */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>FIELDS ({form.fields.length}/25)</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addField} disabled={form.fields.length >= 25}>
              <PlusIcon className="icon" /> Add field
            </button>
          </div>
          {form.fields.map((field, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input className="form-input" placeholder="Field name" value={field.name} onChange={e => updateField(i, 'name', e.target.value)} />
              <input className="form-input" placeholder="Field value" value={field.value} onChange={e => updateField(i, 'value', e.target.value)} />
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={!!field.inline} onChange={e => updateField(i, 'inline', e.target.checked)} />
                Inline
              </label>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeField(i)} style={{ color: 'var(--danger)' }}>
                <XMarkIcon style={{ width: 16, height: 16 }} />
              </button>
            </div>
          ))}

          </>)}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleSavePreset} disabled={saving}>
              {saving ? 'Saving...' : (<><PlusIcon className="icon" /> {editingId ? 'Update preset' : 'Save preset'}</>)}
            </button>
            <button className="btn btn-secondary" onClick={handleSendNow} disabled={sending}>
              {sending ? 'Sending...' : (<><PaperAirplaneIcon className="icon" /> Send now</>)}
            </button>
          </div>
        </div>

        {/* ── Preset list ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* ── Embed Preview ── */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Preview</div>
            <div style={{ background: '#313338', borderRadius: 8, padding: 12, minHeight: 52 }}>
              {form.content && (
                <div style={{ color: '#dbdee1', fontSize: 13, marginBottom: (useEmbed && (form.title || form.description || form.authorName || form.footerText || form.fields.some(f => f.name && f.value))) ? 8 : 0, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                  {form.content}
                </div>
              )}
              {useEmbed && (form.title || form.description || form.authorName || form.footerText || form.fields.some(f => f.name && f.value)) && (
                <div style={{ display: 'flex', gap: 10, background: '#2b2d31', borderRadius: '0 4px 4px 0', borderLeft: `4px solid ${form.color || '#5865F2'}`, padding: '10px 12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {form.authorName && <div style={{ fontSize: 11, color: '#dbdee1', marginBottom: 4, fontWeight: 500 }}>{form.authorName}</div>}
                    {form.title && <div style={{ fontSize: 14, fontWeight: 700, color: '#00b0f4', marginBottom: 4, wordBreak: 'break-word' }}>{form.title}</div>}
                    {form.description && <div style={{ fontSize: 12, color: '#dbdee1', marginBottom: 6, whiteSpace: 'pre-wrap', lineHeight: 1.4, wordBreak: 'break-word' }}>{form.description}</div>}
                    {form.fields.filter(f => f.name && f.value).length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 12px', marginBottom: 4 }}>
                        {form.fields.filter(f => f.name && f.value).map((f, i) => (
                          <div key={i} style={{ gridColumn: f.inline ? undefined : '1 / -1' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#dbdee1', marginBottom: 1 }}>{f.name}</div>
                            <div style={{ fontSize: 12, color: '#dbdee1' }}>{f.value}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {form.footerText && <div style={{ fontSize: 11, color: '#949ba4', marginTop: 4 }}>{form.footerText}</div>}
                  </div>
                  {form.thumbnailURL && <img src={form.thumbnailURL} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                </div>
              )}
              {!form.content && !(useEmbed && (form.title || form.description || form.authorName || form.footerText || form.fields.some(f => f.name && f.value))) && (
                <div style={{ color: '#949ba4', fontSize: 12, textAlign: 'center', padding: '6px 0' }}>Nothing to preview</div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, padding: '4px 0' }}>Saved presets ({presets.length})</div>
          {presets.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12, textAlign: 'center', background: 'var(--bg-card)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              No presets yet
            </div>
          )}
          {presets.map(p => (
            <div key={p._id} className="card" style={{ padding: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                GuildID: <code>{p.guildId || '—'}</code> · ChannelID: <code>{p.channelId || '—'}</code>
              </div>
              {p.embed?.title && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, borderLeft: `3px solid ${p.embed.color || '#5865F2'}`, paddingLeft: 8 }}>{p.embed.title}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(p)} style={{ color: 'var(--accent)' }}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Edit
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleCopyPreset(p)} style={{ color: 'var(--text-muted)' }}>
                  <DocumentDuplicateIcon style={{ width: 14, height: 14 }} /> Copy
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleSendPreset(p._id)} style={{ color: 'var(--success)' }}>
                  <PaperAirplaneIcon style={{ width: 14, height: 14 }} /> Send
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(p._id)} style={{ color: 'var(--danger)' }}>
                  <TrashIcon style={{ width: 14, height: 14 }} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
